/**
 * DOM scanner that bootstraps `LegacyAlchemySelect` islands.
 *
 * Looks for `.js-legacy-alchemy-select` wrappers, hides the underlying
 * `<select>`, and renders an Alchemy Select in its place. Idempotent: a
 * second call on already-mounted wrappers is a no-op.
 *
 * Call once from `application.jsx` at `DOMContentLoaded`. Also exposed on
 * `window` for code paths that insert wrappers into the DOM after initial
 * load (e.g. modal partials loaded via AJAX) to re-trigger mounting.
 */
import { flushSync } from 'react-dom';
import { createRoot } from 'react-dom/client';
import LegacyAlchemySelect from './LegacyAlchemySelect';

const MOUNTED_FLAG = 'legacyAlchemySelectMounted';

export const mountLegacyAlchemySelects = (root: ParentNode = document) => {
  const wrappers = root.querySelectorAll<HTMLElement>('.js-legacy-alchemy-select');
  wrappers.forEach(wrapper => {
    if (wrapper.dataset[MOUNTED_FLAG] === 'true') return;

    const selectEl = wrapper.querySelector<HTMLSelectElement>('select');
    if (!selectEl) {
      // eslint-disable-next-line no-console
      console.warn('LegacyAlchemySelect: no <select> found inside wrapper', wrapper);
      return;
    }

    // Visually hide the native <select> but keep it in the render tree and
    // focusable, so the browser can still run HTML5 constraint validation
    // (e.g. `required`) and anchor its validation bubble to the Alchemy toggle.
    // Using `display:none` would make the control unfocusable and throw
    // "An invalid form control ... is not focusable" on submit.
    if (!wrapper.style.position) wrapper.style.position = 'relative';
    Object.assign(selectEl.style, {
      position: 'absolute',
      top: '0',
      left: '0',
      width: '100%',
      height: '100%',
      margin: '0',
      padding: '0',
      border: '0',
      boxSizing: 'border-box',
      opacity: '0',
      pointerEvents: 'none',
    });
    selectEl.tabIndex = -1;

    const mountDiv = document.createElement('div');
    mountDiv.className = 'js-legacy-alchemy-select-mount';
    selectEl.insertAdjacentElement('afterend', mountDiv);

    const placeholder = wrapper.dataset.placeholder;
    const hideBlankOption = wrapper.dataset.hideBlankOption === 'true';
    const blankOptionLabel = wrapper.dataset.blankOptionLabel;
    const doNotTranslate =
      wrapper.dataset.notranslate === 'true' || selectEl.dataset.notranslate === 'true';

    const root = createRoot(mountDiv);
    // React 18's `createRoot(...).render(...)` schedules the initial mount
    // through the scheduler, so effects and DOM commits may not be flushed
    // synchronously. Legacy Rails views (and browser specs that drive them)
    // rely on the underlying `<select>` being fully wired to a mounted
    // `LegacyAlchemySelect` before subsequent code runs. `flushSync` restores
    // the React 17 `ReactDOM.render` guarantee that mount + effects complete
    // before returning.
    flushSync(() => {
      root.render(
        <LegacyAlchemySelect
          selectEl={selectEl}
          placeholder={placeholder}
          required={selectEl.required}
          hideBlankOption={hideBlankOption}
          blankOptionLabel={blankOptionLabel}
          doNotTranslate={doNotTranslate}
        />
      );
    });

    wrapper.dataset[MOUNTED_FLAG] = 'true';
  });
};
